import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogFooter,
} from '@features/ui/components/dialog';
import { Input } from '@features/ui/components/input';
import { InputSelect } from '@features/ui/components/input-select';
import { Label } from '@features/ui/components/label';
import { Button } from '@features/ui/components/button';
import { SaleTaxRate } from '@src/types/feeSettings';
import * as yup from 'yup';
import { useEffect } from 'react';
import { useStateOptions } from '@src/hooks/useStateOptions';

const schema = yup.object({
  country: yup.string().required(),
  name: yup.string().required('State is required'),
  rate: yup.number().required('Tax value is required'),
});

export type CreateEditTaxFormValues = yup.InferType<typeof schema>;

interface CreateEditTaxModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: CreateEditTaxFormValues) => void;
  initialValues?: Partial<SaleTaxRate>;
  isEdit?: boolean;
  loading?: boolean;
}

export const CreateEditTaxModal = ({
  open,
  onClose,
  onSubmit,
  initialValues,
  isEdit = false,
  loading = false,
}: CreateEditTaxModalProps) => {
  const { stateOptions } = useStateOptions();
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateEditTaxFormValues>({
    resolver: yupResolver(schema),
    defaultValues: initialValues || {
      country: 'US',
      state: '',
      name: '',
      rate: 0,
    },
  });

  useEffect(() => {
    reset(initialValues || { country: 'US', state: '', name: '', rate: 0 });
  }, [initialValues, open, reset]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent size="xl">
        <DialogTitle>{isEdit ? 'Edit Tax' : 'Create New Tax'}</DialogTitle>
        <form
          className="flex flex-col gap-4 mt-2"
          onSubmit={handleSubmit(onSubmit)}
        >
          <Controller
            control={control}
            name="country"
            render={({ field }) => (
              <div className="flex flex-col gap-y-2.5">
                <Label htmlFor="country">Country</Label>
                <Input {...field} disabled id="country" value="US" />
              </div>
            )}
          />
          <Controller
            control={control}
            name="name"
            render={({ field }) => (
              <div className="flex flex-col gap-y-2.5">
                <Label htmlFor="state">State</Label>
                <InputSelect
                  error={!!errors.name}
                  id="name"
                  options={stateOptions}
                  placeholder="Select state"
                  value={field.value ?? ''}
                  onChange={field.onChange}
                />
                {errors.name && (
                  <span className="text-xs text-red-500">
                    {errors.name.message}
                  </span>
                )}
              </div>
            )}
          />
          <Controller
            control={control}
            name="rate"
            render={({ field }) => (
              <div className="flex flex-col gap-y-2.5">
                <Label htmlFor="rate">Tax Value (%)</Label>
                <Input
                  {...field}
                  error={!!errors.rate}
                  id="rate"
                  max={100}
                  min={0}
                  step="0.001"
                  type="number"
                />
                {errors.rate && (
                  <span className="text-xs text-red-500">
                    {errors.rate.message}
                  </span>
                )}
              </div>
            )}
          />
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button loading={loading} type="submit" variant="lime">
              {isEdit ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
